import { TrendingDown, TrendingUp, Award } from 'lucide-react';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';

interface IndustryBenchmarkProps {
    customerCategory: string;
    industryAverageCycleTime: number; // in days
    customerCycleTime: number; // in days
}

export function IndustryBenchmark({
    customerCategory,
    industryAverageCycleTime,
    customerCycleTime,
}: IndustryBenchmarkProps) {
    const difference = industryAverageCycleTime - customerCycleTime;
    const percentageDifference = ((difference / industryAverageCycleTime) * 100).toFixed(1);
    const isAboveAverage = customerCycleTime < industryAverageCycleTime;

    return (
        <Card className="bg-white shadow-md">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            Industry Benchmark Comparison
                            {isAboveAverage && <Award className="w-5 h-5 text-yellow-500" />}
                        </CardTitle>
                        <CardDescription>Performance vs industry standards</CardDescription>
                    </div>
                    <Badge variant="secondary" className="px-3 py-1">
                        {customerCategory}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Industry Average */}
                    <div className="space-y-2">
                        <p className="text-sm text-gray-600">Industry Average Cycle Time</p>
                        <div className="flex items-baseline gap-2">
                            <span className="text-3xl text-gray-900">{industryAverageCycleTime}</span>
                            <span className="text-gray-600">days</span>
                        </div>
                        <p className="text-xs text-gray-500">{customerCategory} Benchmark</p>
                    </div>

                    {/* Customer Performance */}
                    <div className="space-y-2">
                        <p className="text-sm text-gray-600">Joe's Donuts Cycle Time</p>
                        <div className="flex items-baseline gap-2">
                            <span className="text-3xl text-[#0072CE]">{customerCycleTime}</span>
                            <span className="text-gray-600">days</span>
                        </div>
                        <p className="text-xs text-gray-500">Current Performance</p>
                    </div>

                    {/* Difference */}
                    <div className="space-y-2">
                        <p className="text-sm text-gray-600">Difference</p>
                        <div className="flex items-baseline gap-2">
                            {isAboveAverage ? (
                                <TrendingDown className="w-6 h-6 text-green-600" />
                            ) : (
                                <TrendingUp className="w-6 h-6 text-red-600" />
                            )}
                            <span className={`text-3xl ${isAboveAverage ? 'text-green-600' : 'text-red-600'}`}>
                                {Math.abs(difference)}
                            </span>
                            <span className="text-gray-600">days</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge
                                variant={isAboveAverage ? 'default' : 'destructive'}
                                className={isAboveAverage ? 'bg-green-600' : ''}
                            >
                                {isAboveAverage ? '↓' : '↑'} {percentageDifference}%
                            </Badge>
                            <p className="text-xs text-gray-500">
                                {isAboveAverage ? 'faster than average' : 'slower than average'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Performance Message */}
                <div className={`mt-6 p-4 rounded-lg ${isAboveAverage ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                    <p className="text-sm text-gray-700">
                        {isAboveAverage ? (
                            <>
                                <strong className="text-green-700">Excellent Performance!</strong> Joe's Donuts' cycle time is{' '}
                                <strong>{Math.abs(difference)} days faster</strong> than the {customerCategory} industry average,
                                demonstrating superior operational efficiency and sustainability practices.
                            </>
                        ) : (
                            <>
                                <strong className="text-orange-700">Opportunity for Improvement:</strong> Joe's Donuts' cycle time is{' '}
                                <strong>{Math.abs(difference)} days slower</strong> than the {customerCategory} industry average.
                                Optimizing processes could reduce emissions and improve efficiency.
                            </>
                        )}
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}
